import { Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../components/button';
import { SearchInput } from '../../../components/search-input';
import { PaginationBar } from '../../../components/pagination/pagination-bar';
import { Tag } from '../../../components/tag';
import { useContestStore } from '../../../contexts/mobx';
import type {
    ContestEntry,
    ContestEntrySearchParams,
} from '../../../stores/contest';
import { formatAppTargets } from '../../../utils/app-target';
import { convertUtcToLocalMoment } from '../../../utils/timezone';
import { useSearchParams } from '../../../utils/use-search-params';
import { EntryDetail } from './entry-detail';
import {
    BLOCK_TYPE_LABELS,
    CHANNEL_LABELS,
    ENTRY_PATH_LABELS,
    GRANT_SOURCE_LABELS,
} from './labels';
import style from './style.module.scss';

const EMPTY = '—';

function columns(timeZone: string): ColumnProps<ContestEntry>[] {
    return [
        {
            title: 'Date',
            key: 'createdAt',
            render: (_, entry) =>
                convertUtcToLocalMoment(entry.createdAt, timeZone)?.format(
                    'lll'
                ),
        },
        {
            title: 'Phone',
            key: 'phone',
            render: (_, entry) =>
                entry.participant.anonymizedAt ? (
                    <Tag variant="grey">Anonymised</Tag>
                ) : (
                    entry.participant.phoneNumber
                ),
        },
        {
            title: 'Email',
            key: 'email',
            render: (_, entry) => entry.participant.email ?? EMPTY,
        },
        {
            title: 'Postal code',
            key: 'postalCode',
            render: (_, entry) => entry.participant.postalCode ?? EMPTY,
        },
        {
            title: 'Province',
            key: 'province',
            render: (_, entry) => entry.participant.province,
        },
        {
            title: 'Language',
            key: 'lang',
            render: (_, entry) => entry.participant.lang.toUpperCase(),
        },
        {
            title: 'Banner',
            key: 'appTarget',
            render: (_, entry) =>
                entry.appTarget ? formatAppTargets([entry.appTarget]) : EMPTY,
        },
        {
            title: 'Channel',
            key: 'channel',
            render: (_, entry) =>
                entry.channel ? CHANNEL_LABELS[entry.channel] : EMPTY,
        },
        {
            title: 'Block',
            key: 'block',
            render: (_, entry) =>
                entry.block ? (
                    <>
                        <div>{entry.block.key}</div>
                        <div className="lil_label">
                            {BLOCK_TYPE_LABELS[entry.block.type]}
                        </div>
                    </>
                ) : (
                    EMPTY
                ),
        },
        {
            title: 'Entry path',
            key: 'entryPath',
            render: (_, entry) => ENTRY_PATH_LABELS[entry.entryPath],
        },
        {
            title: 'Source',
            key: 'grantSource',
            render: (_, entry) => GRANT_SOURCE_LABELS[entry.grantSource],
        },
        {
            title: 'Proof of purchase',
            key: 'proofValue',
            render: (_, entry) => entry.proofValue ?? EMPTY,
        },
        {
            title: 'Skill question',
            key: 'skill',
            align: 'center',
            render: (_, entry) =>
                entry.participant.skillPassedAt ? (
                    <Tag variant="success">Passed</Tag>
                ) : (
                    EMPTY
                ),
        },
        {
            title: 'Chances',
            key: 'quantity',
            align: 'right',
            render: (_, entry) => entry.quantity,
        },
    ];
}

export const EntriesTab = observer(() => {
    const store = useContestStore();
    const contestId = Number(useParams<{ id: string }>().id);
    const search = useSearchParams<ContestEntrySearchParams>();
    const { params, setParams } = search;
    const [input, setInput] = React.useState(
        params.search === undefined ? '' : String(params.search)
    );
    const debounceRef = React.useRef<number | null>(null);

    React.useEffect(
        () => () => {
            if (debounceRef.current !== null) {
                window.clearTimeout(debounceRef.current);
            }
        },
        []
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInput(value);
        if (debounceRef.current !== null) {
            window.clearTimeout(debounceRef.current);
        }
        debounceRef.current = window.setTimeout(() => {
            setParams({ search: value.trim() || undefined, page: 0 });
        }, 350);
    };
    const [openEntryId, setOpenEntryId] = React.useState<string | null>(null);

    React.useEffect(() => {
        store.fetchEntries(contestId, params);
    }, [contestId, params, store]);

    const timeZone = store.contest?.timeZone;
    const entries = store.entriesContestId === contestId ? store.entries : null;

    if (!timeZone) {
        return null;
    }

    return (
        <>
            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={`query_box ${style.search}`}>
                        <SearchInput
                            searchValue={input}
                            placeholder="Email, phone, postal code, proof or ID"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className={style.exports}>
                        <Button
                            loading={store.exportingProfile === 'analysis'}
                            disabled={store.exportingProfile !== null}
                            onClick={() =>
                                store.downloadExport(contestId, 'analysis')
                            }
                        >
                            Export for analysis
                        </Button>
                        <Button
                            loading={store.exportingProfile === 'draw'}
                            disabled={store.exportingProfile !== null}
                            onClick={() =>
                                store.downloadExport(contestId, 'draw')
                            }
                        >
                            Export for the draw
                        </Button>
                    </div>
                </div>
                <PaginationBar
                    pagination={{
                        page: params.page,
                        pageSize: params.perPage,
                        total: entries?.total,
                        onPageChange: (page) => setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<ContestEntry>
                className="clickable_table_body_rows"
                rowKey="id"
                dataSource={entries?.results}
                columns={columns(timeZone)}
                pagination={false}
                loading={store.isFetchingEntries}
                onRow={(entry) => ({
                    onClick: () => setOpenEntryId(entry.id),
                })}
            />

            <EntryDetail
                contestId={contestId}
                entryId={openEntryId}
                timeZone={timeZone}
                onClose={() => setOpenEntryId(null)}
                onAnonymized={() => store.fetchEntries(contestId, params)}
            />
        </>
    );
});
